import React, { useState, useEffect } from 'react';
import { 
  getSubTargetsByPlanId, 
  getSubTargetsSummary,
  createSubTarget,
  updateSubTarget,
  updateSubTargetProgress,
  deleteSubTarget
} from '../api/sub-targets/SubTargetsApi';
import GetPlansDetailsApi from '../api/plans/GetPlanDetailsApi';
import { useSelector } from 'react-redux';
import { permissionAbility } from '../helpers/permissionHelpers';
import { message } from 'antd';

const SubTargetsComponent = ({ planId, planData }) => {
  const [subTargets, setSubTargets] = useState([]);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showAddModal, setShowAddModal] = useState(false);
  const [editingTarget, setEditingTarget] = useState(null);
  const [fullPlanData, setFullPlanData] = useState(null);
  const [showProgressModal, setShowProgressModal] = useState(false);
  const [selectedSubTarget, setSelectedSubTarget] = useState(null);
  const [progressSubmitting, setProgressSubmitting] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [targetToDelete, setTargetToDelete] = useState(null);
  const positionState = useSelector((state) => state.positionState.value);
  const userLevel = positionState?.userRole?.name;
  const canEditDelete = userLevel === "Admin" || userLevel === "Super-Admin";
  const [targetValueError, setTargetValueError] = useState('');
  const [progressDisplayValue, setProgressDisplayValue] = useState('');

  // Format a number string with commas, preserving decimals
  const formatWithCommas = (val) => {
    if (val === '' || val === null || val === undefined) return '';
    const str = String(val).replace(/,/g, '');
    const [integer, decimal] = str.split('.');
    const formatted = integer.replace(/\B(?=(\d{3})+(?!\d))/g, ',');
    return decimal !== undefined ? `${formatted}.${decimal}` : formatted;
  };

  // Strip commas to get raw numeric string
  const stripCommas = (val) => String(val).replace(/,/g, '');
  const [formData, setFormData] = useState({
    targetName: '',
    targetValue: '',
    targetDate: '',
    description: '',
    sortOrder: 0
  });

  useEffect(() => {
    if (planId) {
      loadPlanDetails();
      loadSubTargets();
      loadSummary();
    }
  }, [planId]);

  const loadPlanDetails = async () => {
    try {
      const apiResponse = await GetPlansDetailsApi(planId, positionState.id);
      if (apiResponse.success) {
        setFullPlanData(apiResponse.data);
      }
    } catch (error) {
      console.error('Error loading plan details:', error);
    }
  };

  const loadSubTargets = async () => {
    try {
      const data = await getSubTargetsByPlanId(planId);
      setSubTargets(data);
    } catch (error) {
      console.error('Error loading sub-targets:', error);
    }
  };

  const loadSummary = async () => {
    try {
      const data = await getSubTargetsSummary(planId);
      setSummary(data);
    } catch (error) {
      console.error('Error loading summary:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleAddSubTarget = () => {
    setFormData({
      targetName: '',
      targetValue: '',
      targetDate: '',
      description: '',
      sortOrder: subTargets.length
    });
    setEditingTarget(null);
    setTargetValueError('');
    setShowAddModal(true);
  };

  const handleEditSubTarget = (subTarget) => {
    setFormData({
      targetName: subTarget.targetName,
      targetValue: formatWithCommas(subTarget.targetValue),
      targetDate: subTarget.targetDate.split('T')[0],
      description: subTarget.description || '',
      sortOrder: subTarget.sortOrder
    });
    setEditingTarget(subTarget);
    setTargetValueError('');
    setShowAddModal(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Simple validation
    if (!formData.targetDate) {
      message.error('Please select a target date');
      return;
    }
    
    // Validation against plan limits
    const planDetails = fullPlanData || planData;
    const targetValue = parseFloat(stripCommas(formData.targetValue));
    const targetDate = new Date(formData.targetDate);
    const planTargetDate = new Date(planDetails?.targetDate);
    const planTargetValue = parseFloat(planDetails?.target);

    // Validate target date is within plan target date
    if (targetDate > planTargetDate) {
      message.error(`Sub-target date cannot be later than plan target date (${planTargetDate.toLocaleDateString()})`);
      return;
    }

    // BUDGET VALIDATION: Check if sub-target exceeds available budget (unless canExceed is enabled)
    if (!planDetails?.canExceed) {
      // Calculate what's already used by other sub-targets (exclude current if editing)
      const usedByOtherSubTargets = subTargets
        .filter(st => editingTarget ? st.id !== editingTarget.id : true)
        .reduce((sum, st) => sum + (parseFloat(stripCommas(st.targetValue)) || 0), 0);
      
      const availableBudget = planTargetValue - usedByOtherSubTargets;

      if (targetValue > availableBudget) {
        setTargetValueError(`cannot exceed more than ${availableBudget}`);
        return;
      }
    }
    setTargetValueError('');
    
    try {
      // Use plan's scale for sub-targets (scaleUnitId is inherited from plan, not stored)
      const planDetails = fullPlanData || planData;
      
      // Extract scale ID directly from plan data
      const scaleId = planDetails?.scaleId || 
                      planDetails?.scale?.scaleId || 
                      planDetails?.targetScale;
      
      console.log('Plan Details for Sub-Target:', {
        fullPlanData: planDetails,
        scaleId,
        rawScaleId: planDetails?.scaleId
      });
      
      if (!scaleId) {
        message.error('Unable to determine plan scale. Please ensure the plan has a valid scale.');
        return;
      }
      
      const submitData = {
        ...formData,
        planId,
        targetValue: targetValue,
        scaleId: scaleId,
        addedBy: positionState.id, // Use actual position ID
        sortOrder: parseInt(formData.sortOrder)
      };

      if (editingTarget) {
        await updateSubTarget(editingTarget.id, submitData);
      } else {
        await createSubTarget(submitData);
      }

      setShowAddModal(false);
      loadSubTargets();
      loadSummary();
    } catch (error) {
      console.error('Error saving sub-target:', error);
      message.error('Error saving sub-target: ' + error.message);
    }
  };

  const handleProgressUpdate = async (subTargetId, newProgress) => {
    try {
      await updateSubTargetProgress(subTargetId, newProgress);
      loadSubTargets();
      loadSummary();
    } catch (error) {
      console.error('Error updating progress:', error);
      message.error('Error updating progress: ' + error.message);
    }
  };

  const handleOpenProgressModal = (subTarget) => {
    setSelectedSubTarget(subTarget);
    setProgressDisplayValue(formatWithCommas(subTarget.currentProgress || 0));
    setShowProgressModal(true);
  };

  const handleProgressModalSubmit = async (values) => {
    setProgressSubmitting(true);
    try {
      await updateSubTargetProgress(selectedSubTarget.id, values.progress);
      loadSubTargets();
      loadSummary();
      setShowProgressModal(false);
      setSelectedSubTarget(null);
    } catch (error) {
      console.error('Error updating progress:', error);
      message.error('Error updating progress: ' + error.message);
    }
    setProgressSubmitting(false);
  };

  const handleProgressModalCancel = () => {
    setShowProgressModal(false);
    setSelectedSubTarget(null);
  };

  const handleDelete = (subTarget) => {
    setTargetToDelete(subTarget);
    setShowDeleteModal(true);
  };

  const confirmDelete = async () => {
    try {
      await deleteSubTarget(targetToDelete.id);
      loadSubTargets();
      loadSummary();
      setShowDeleteModal(false);
      setTargetToDelete(null);
    } catch (error) {
      console.error('Error deleting sub-target:', error);
      message.error('Error deleting sub-target: ' + error.message);
    }
  };

  const cancelDelete = () => {
    setShowDeleteModal(false);
    setTargetToDelete(null);
  };

  const formatValue = (value, scale) => {
    if (!value) return '0';
    
    const numValue = parseFloat(value);
    
    // Use the same logic as targetHelpers.js
    if (!scale || !scale.scaleName) {
      // Fallback to basic number formatting
      return getNumberInText(numValue);
    }
    
    const scaleName = scale.scaleName;
    const scaleUnitAbbreviation = scale.scaleUnitAbbreviation || '';
    
    if (scaleName === "Number") {
      return getNumberInText(numValue);
    } else if (scaleName === "Currency") {
      return getAmountInText(numValue, scaleUnitAbbreviation);
    } else if (scaleName === "Percentage") {
      return numValue + "%";
    } else {
      return numValue + scaleUnitAbbreviation;
    }
  };

  // Helper functions from targetHelpers.js
  const getAmountInText = (amount, abb) => {
    let currencyType = Intl.NumberFormat("en-US", {
      style: "currency",
      currency: abb || "USD",
    });
    // Nine Zeroes for Billions
    return Math.abs(Number(amount)) >= 1.0e9
      ? currencyType.format(0).split("0")[0] +
          (Math.abs(Number(amount)) / 1.0e9).toFixed(2) +
          "B"
      : // Six Zeroes for Millions
      Math.abs(Number(amount)) >= 1.0e6
      ? currencyType.format(0).split("0")[0] +
        (Math.abs(Number(amount)) / 1.0e6).toFixed(2) +
        "M"
      : currencyType.format(amount);
  };

  const getNumberInText = (amount) => {
    let type = Intl.NumberFormat("en-US");
    // Nine Zeroes for Billions
    return Math.abs(Number(amount)) >= 1.0e9
      ? (Math.abs(Number(amount)) / 1.0e9).toFixed(2) + "B"
      : // Six Zeroes for Millions
      Math.abs(Number(amount)) >= 1.0e6
      ? (Math.abs(Number(amount)) / 1.0e6).toFixed(2) + "M"
      : type.format(amount);
  };

  const calculateProgress = (current, target) => {
    if (!target || target === 0) return 0;
    const progress = (current / target) * 100;
    return parseFloat(progress.toFixed(2));
  };

  const calculateOverallProgress = () => {
    if (!subTargets || subTargets.length === 0) return 0;
    
    // Calculate weighted average based on target values
    let totalWeightedProgress = 0;
    let totalWeight = 0;
    
    subTargets.forEach(subTarget => {
      const weight = parseFloat(subTarget.targetValue) || 0;
      const progress = calculateProgress(subTarget.currentProgress || 0, subTarget.targetValue);
      
      totalWeightedProgress += (progress * weight);
      totalWeight += weight;
    });
    
    if (totalWeight === 0) return 0;
    const overallProgress = totalWeightedProgress / totalWeight;
    return parseFloat(overallProgress.toFixed(2));
  };

  const getProgressColors = (progress) => {
    if (progress < 30) {
      return {
        background: 'bg-red-50',
        border: 'border-red-200',
        text: 'text-red-800',
        label: 'text-red-600',
        bar: 'bg-red-600',
        barBg: 'bg-red-200'
      };
    } else if (progress >= 30 && progress < 60) {
      return {
        background: 'bg-yellow-50',
        border: 'border-yellow-200',
        text: 'text-yellow-800',
        label: 'text-yellow-600',
        bar: 'bg-yellow-600',
        barBg: 'bg-yellow-200'
      };
    } else {
      return {
        background: 'bg-green-50',
        border: 'border-green-200',
        text: 'text-green-800',
        label: 'text-green-600',
        bar: 'bg-green-600',
        barBg: 'bg-green-200'
      };
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Summary Cards */}
      {summary && (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
          {/* Plan Info - Indigo Theme */}
          <div className="bg-indigo-50 p-3 rounded-lg border-2 border-indigo-200 shadow-sm">
            <h3 className="text-xs font-medium text-indigo-600 mb-1">Plan Info</h3>
            <p className="text-sm font-bold text-indigo-800 leading-tight break-words">
              {(fullPlanData || planData)?.title || 'Plan Details'}
            </p>
            <p className="text-xs text-indigo-600 mt-1">
              Target: {formatValue((fullPlanData || planData)?.target, (fullPlanData || planData)?.scale)}
            </p>
          </div>
          
          {/* Overall Progress - Dynamic Color Theme */}
          <div className={`${getProgressColors(calculateOverallProgress()).background} p-3 rounded-lg border-2 ${getProgressColors(calculateOverallProgress()).border} shadow-sm`}>
            <h3 className={`text-xs font-medium ${getProgressColors(calculateOverallProgress()).label} mb-1`}>Overall Progress</h3>
            <p className={`text-xl font-bold ${getProgressColors(calculateOverallProgress()).text}`}>{calculateOverallProgress().toFixed(2)}%</p>
            <div className={`w-full ${getProgressColors(calculateOverallProgress()).barBg} rounded-full h-1.5 mt-2`}>
              <div 
                className={`${getProgressColors(calculateOverallProgress()).bar} h-1.5 rounded-full transition-all duration-300`}
                style={{ width: `${Math.min(calculateOverallProgress(), 100)}%` }}
              ></div>
            </div>
          </div>
        </div>
      )}

      {/* Header with Add Button */}
      <div className="flex justify-between items-center">
        <h2 className="text-xl font-semibold text-slate-900">Sub-Targets</h2>
        <button
          onClick={handleAddSubTarget}
          className="bg-emerald-600 text-white px-4 py-2 rounded-lg hover:bg-emerald-700 transition-colors font-medium shadow-sm"
        >
          Add Sub-Target
        </button>
      </div>

      {/* Sub-Targets Table */}
      <div className="bg-slate-50 rounded-lg border-2 border-slate-200 shadow-sm overflow-hidden">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-slate-200">
            <thead className="bg-slate-100">
              <tr>
                <th className="px-4 py-2 text-left text-xs font-semibold text-slate-700 uppercase tracking-wider">
                  Target Name
                </th>
                <th className="px-4 py-2 text-left text-xs font-semibold text-slate-700 uppercase tracking-wider">
                  Target Value
                </th>
                <th className="px-4 py-2 text-left text-xs font-semibold text-slate-700 uppercase tracking-wider">
                  Current Progress
                </th>
                <th className="px-4 py-2 text-left text-xs font-semibold text-slate-700 uppercase tracking-wider">
                  Progress %
                </th>
                <th className="px-4 py-2 text-left text-xs font-semibold text-slate-700 uppercase tracking-wider">
                  Target Date
                </th>
                {canEditDelete && (
                  <th className="px-4 py-2 text-left text-xs font-semibold text-slate-700 uppercase tracking-wider">
                    Actions
                  </th>
                )}
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-slate-200">
              {subTargets.map((subTarget) => (
                <tr key={subTarget.id} className="hover:bg-slate-50 transition-colors">
                  <td className="px-4 py-3 whitespace-nowrap">
                    <div>
                      <div className="text-sm font-semibold text-slate-900">
                        {subTarget.targetName}
                      </div>
                      {subTarget.description && (
                        <div className="text-xs text-slate-600 mt-1">
                          {subTarget.description}
                        </div>
                      )}
                    </div>
                  </td>
                  <td className="px-4 py-3 whitespace-nowrap text-sm font-medium text-slate-800">
                    {formatValue(subTarget.targetValue, (fullPlanData || planData)?.scale)}
                  </td>
                  <td className="px-4 py-3 whitespace-nowrap">
                    <div className="flex items-center space-x-2">
                      <span className="text-sm font-medium text-slate-800 inline-block w-16">
                        {formatValue(subTarget.currentProgress || 0, (fullPlanData || planData)?.scale)}
                      </span>
                      <button
                        onClick={() => handleOpenProgressModal(subTarget)}
                        className="text-xs bg-emerald-600 text-white px-2 py-1 rounded-md hover:bg-emerald-700 transition-colors font-medium"
                      >
                        Update
                      </button>
                    </div>
                  </td>
                  <td className="px-4 py-3 whitespace-nowrap">
                    <div className="flex items-center">
                      <div className="w-12 bg-slate-200 rounded-full h-1.5 mr-2">
                        <div
                          className="bg-emerald-600 h-1.5 rounded-full transition-all duration-300"
                          style={{
                            width: `${Math.min(calculateProgress(subTarget.currentProgress, subTarget.targetValue), 100)}%`
                          }}
                        ></div>
                      </div>
                      <span className="text-sm font-medium text-slate-800">
                        {calculateProgress(subTarget.currentProgress, subTarget.targetValue).toFixed(2)}%
                      </span>
                    </div>
                  </td>
                  <td className="px-4 py-3 whitespace-nowrap text-sm text-slate-700">
                    {new Date(subTarget.targetDate).toLocaleDateString()}
                  </td>
                  {canEditDelete && (
                    <td className="px-4 py-3 whitespace-nowrap text-sm font-medium">
                      <button
                        onClick={() => handleEditSubTarget(subTarget)}
                        className="text-amber-600 hover:text-amber-800 mr-3 font-medium"
                      >
                        Edit
                      </button>
                      <button
                        onClick={() => handleDelete(subTarget)}
                        className="text-red-600 hover:text-red-800 font-medium"
                      >
                        Delete
                      </button>
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
          
          {subTargets.length === 0 && (
            <div className="text-center py-8">
              <p className="text-slate-500 text-sm">No sub-targets found. Add your first sub-target to get started.</p>
            </div>
          )}
        </div>
      </div>

      {/* Add/Edit Modal */}
      {showAddModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 overflow-y-auto h-full w-full z-50 flex items-center justify-center p-4">
          <div className="relative mx-auto max-w-2xl w-full bg-white rounded-xl shadow-2xl">
            {/* Modal Header */}
            <div className="flex items-center justify-between p-6 border-b border-gray-200">
              <div>
                <h3 className="text-xl font-semibold text-gray-900">
                  {editingTarget ? 'Edit Sub-Target' : 'Add New Sub-Target'}
                </h3>
                <p className="text-sm text-gray-500 mt-1">
                  {editingTarget ? 'Update the details of your sub-target' : 'Create a new sub-target to track progress'}
                </p>
              </div>
              <button
                onClick={() => setShowAddModal(false)}
                className="text-gray-400 hover:text-gray-600 transition-colors"
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
            
            {/* Modal Body */}
            <div className="p-6">
              
              <form onSubmit={handleSubmit} className="space-y-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div className="md:col-span-2">
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Target Name <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="text"
                      required
                      value={formData.targetName}
                      onChange={(e) => setFormData({...formData, targetName: e.target.value})}
                      className="block w-full px-4 py-3 border border-gray-300 rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
                      placeholder="Enter a descriptive name for your sub-target"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Target Value <span className="text-red-500">*</span>
                    </label>
                    {(fullPlanData || planData)?.target && (
                      <p className="text-xs text-blue-600 mb-2 bg-blue-50 p-2 rounded">
                        Plan's main target: {formatValue((fullPlanData || planData).target, (fullPlanData || planData)?.scale)}
                      </p>
                    )}
                    <input
                      type="text"
                      inputMode="decimal"
                      required
                      value={formData.targetValue}
                      onChange={(e) => {
                        const raw = e.target.value.replace(/,/g, '');
                        if (raw === '' || /^\d*\.?\d*$/.test(raw)) {
                          setFormData({...formData, targetValue: formatWithCommas(raw)});
                          setTargetValueError('');
                        }
                      }}
                      className={`block w-full px-4 py-3 border ${targetValueError ? 'border-red-500' : 'border-gray-300'} rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors`}
                      placeholder="Enter target value"
                    />
                    {targetValueError && (
                      <p className="text-xs text-red-500 mt-1">{targetValueError}</p>
                    )}
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Target Date <span className="text-red-500">*</span>
                    </label>
                    {(fullPlanData || planData)?.targetDate && (
                      <p className="text-xs text-blue-600 mb-2 bg-blue-50 p-2 rounded">
                        Plan deadline: {new Date((fullPlanData || planData).targetDate).toLocaleDateString()}
                      </p>
                    )}
                    <input
                      type="date"
                      required
                      max={(fullPlanData || planData)?.targetDate ? (fullPlanData || planData).targetDate.split('T')[0] : ''}
                      value={formData.targetDate}
                      onChange={(e) => setFormData({...formData, targetDate: e.target.value})}
                      className="block w-full px-4 py-3 border border-gray-300 rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Description (Optional)
                  </label>
                  <textarea
                    value={formData.description}
                    onChange={(e) => setFormData({...formData, description: e.target.value})}
                    rows={2}
                    className="block w-full px-4 py-3 border border-gray-300 rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors resize-none"
                    placeholder="Add any additional details or notes about this sub-target..."
                  />
                </div>
              </form>
            </div>
            
            {/* Modal Footer */}
            <div className="flex items-center justify-end space-x-4 p-6 border-t border-gray-200 bg-gray-50 rounded-b-xl">
              <button
                type="button"
                onClick={() => setShowAddModal(false)}
                className="px-6 py-2.5 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-gray-500 transition-colors"
              >
                Cancel
              </button>
              <button
                type="submit"
                form="sub-target-form"
                onClick={handleSubmit}
                className="px-6 py-2.5 text-sm font-medium text-white bg-blue-600 rounded-lg hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 transition-colors flex items-center space-x-2"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
                <span>{editingTarget ? 'Update Sub-Target' : 'Create Sub-Target'}</span>
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Progress Update Modal */}
      {showProgressModal && selectedSubTarget && (
        <div className="fixed inset-0 bg-black bg-opacity-50 overflow-y-auto h-full w-full z-50 flex items-center justify-center p-4">
          <div className="relative mx-auto max-w-md w-full bg-white rounded-xl shadow-2xl">
            {/* Modal Header */}
            <div className="flex items-center justify-between p-6 border-b border-gray-200">
              <div>
                <h3 className="text-xl font-semibold text-gray-900">
                  Update Progress
                </h3>
                <p className="text-sm text-gray-500 mt-1">
                  {selectedSubTarget.targetName}
                </p>
              </div>
              <button
                onClick={handleProgressModalCancel}
                className="text-gray-400 hover:text-gray-600 transition-colors"
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
            
            {/* Modal Body */}
            <div className="p-6">
              
              <form onSubmit={(e) => {
                e.preventDefault();
                const progress = parseFloat(stripCommas(progressDisplayValue));
                if (isNaN(progress)) return;
                handleProgressModalSubmit({ progress });
              }} className="space-y-4">
                
                <div className="flex items-center space-x-2">
                  <div className="flex-1">
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Progress:
                    </label>
                    <input
                      type="text"
                      inputMode="decimal"
                      value={progressDisplayValue}
                      onChange={(e) => {
                        const raw = e.target.value.replace(/,/g, '');
                        if (raw === '' || /^-?\d*\.?\d*$/.test(raw)) {
                          setProgressDisplayValue(formatWithCommas(raw));
                        }
                      }}
                      required
                      className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                    />
                  </div>
                  <div className="text-lg text-gray-600 mt-6">
                    / {formatValue(selectedSubTarget.targetValue, selectedSubTarget.scaleUnit)}
                  </div>
                </div>

                <div className="text-sm text-gray-500">
                  Target: {formatValue(selectedSubTarget.targetValue, selectedSubTarget.scaleUnit)}
                </div>

                <div className="flex justify-end space-x-3 pt-4">
                  <button
                    type="button"
                    onClick={handleProgressModalCancel}
                    disabled={progressSubmitting}
                    className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-200 rounded-md hover:bg-gray-300 focus:outline-none focus:ring-2 focus:ring-gray-500 disabled:opacity-50"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    disabled={progressSubmitting}
                    className="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:opacity-50 flex items-center"
                  >
                    {progressSubmitting && (
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                    )}
                    Update
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {showDeleteModal && targetToDelete && (
        <div className="fixed inset-0 bg-black bg-opacity-50 overflow-y-auto h-full w-full z-50 flex items-center justify-center p-4">
          <div className="relative mx-auto max-w-md w-full bg-white rounded-xl shadow-2xl">
            {/* Modal Header */}
            <div className="flex items-center justify-between p-6 border-b border-gray-200">
              <div>
                <h3 className="text-xl font-semibold text-gray-900">Delete Sub-Target</h3>
                <p className="text-sm text-gray-500 mt-1">This action cannot be undone</p>
              </div>
              <button
                onClick={cancelDelete}
                className="text-gray-400 hover:text-gray-600 transition-colors"
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            {/* Modal Body */}
            <div className="p-6">
              <div className="flex items-center space-x-4">
                <div className="flex-shrink-0">
                  <div className="w-12 h-12 bg-red-100 rounded-full flex items-center justify-center">
                    <svg className="w-6 h-6 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z" />
                    </svg>
                  </div>
                </div>
                <div className="flex-1">
                  <h4 className="text-lg font-medium text-gray-900 mb-2">
                    Are you sure you want to delete "{targetToDelete.targetName}"?
                  </h4>
                  <p className="text-sm text-gray-600">
                    This will permanently remove this sub-target and all its progress data. This action cannot be undone.
                  </p>
                </div>
              </div>
            </div>

            {/* Modal Footer */}
            <div className="flex items-center justify-end space-x-4 p-6 border-t border-gray-200 bg-gray-50 rounded-b-xl">
              <button
                type="button"
                onClick={cancelDelete}
                className="px-6 py-2.5 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-gray-500 transition-colors"
              >
                No, Cancel
              </button>
              <button
                type="button"
                onClick={confirmDelete}
                className="px-6 py-2.5 text-sm font-medium text-white bg-red-600 rounded-lg hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-red-500 transition-colors flex items-center space-x-2"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                </svg>
                <span>Yes, Delete</span>
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default SubTargetsComponent;
